/**
 * How the virtual keyboard hides the page button it "borrows".
 *
 * When the keyboard opens, it copies the label and the click of the next `input-sequence` button
 * (for example a set's Log button) onto its own confirm button, and hides the page copy so the
 * screen never shows two of them. By default the page copy is hidden with `display: none`, which
 * also removes its space from the layout.
 *
 * An element that carries the `keep-space-when-borrowed` attribute is hidden with
 * `visibility: hidden` instead, so it keeps its space and the fields beside it do not change
 * width while the keyboard is open (#4142 follow-up: the Reps field grew into the space of the
 * Log button beside it).
 */

/** the attribute an element carries to keep its space while the keyboard borrows it */
export const KEEP_SPACE_WHEN_BORROWED_ATTRIBUTE = 'keep-space-when-borrowed';

/** which style property hid the borrowed element */
export enum BorrowedElementHideStyle {
  /** `display: none` - the element's space collapses (the default) */
  Display = 'display',
  /** `visibility: hidden` - the element keeps its space */
  Visibility = 'visibility',
}

/** what is needed to undo one hide */
export interface BorrowedElementHiding {
  hideStyle: BorrowedElementHideStyle;
  /** the inline value the property had before the keyboard changed it */
  originalValue: string;
}

/** the part of an HTMLElement these helpers use, so they can be tested without a DOM */
export interface HideableElement {
  style: { display: string; visibility: string };
  hasAttribute(name: string): boolean;
}

/** hide a borrowed element and return what is needed to restore it */
export function hideBorrowedElement(element: HideableElement): BorrowedElementHiding {
  // if the element asks to keep its space, hide it without taking it out of the layout
  if (element.hasAttribute(KEEP_SPACE_WHEN_BORROWED_ATTRIBUTE)) {
    const hiding = { hideStyle: BorrowedElementHideStyle.Visibility, originalValue: element.style.visibility };
    element.style.visibility = 'hidden';
    return hiding;
  }
  const hiding = { hideStyle: BorrowedElementHideStyle.Display, originalValue: element.style.display };
  element.style.display = 'none';
  return hiding;
}

/** put back the inline style a hide changed */
export function restoreBorrowedElement(element: HideableElement, hiding: BorrowedElementHiding): void {
  if (hiding.hideStyle === BorrowedElementHideStyle.Visibility) {
    element.style.visibility = hiding.originalValue;
  } else {
    element.style.display = hiding.originalValue;
  }
}
